import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { Calendar, Info, ArrowRight } from "lucide-react";
import { format, addDays, isBefore, isAfter, startOfDay, differenceInDays, startOfMonth, endOfMonth, addMonths } from "date-fns";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useBooking } from "@/hooks/use-booking-context";
import { useUnavailableDatesForCalendar } from "@/hooks/use-availability";
import { 
  formatDate, 
  getTomorrowDate, 
  getMinCheckoutDate, 
  isDateInPast,
  getYearFromDate,
  parseDateOnly,
  calculateNights
} from "@/lib/date-utils";
import { DateRange } from "react-day-picker";
import { calendarSelectionMode, validateCalendarStay } from "@/lib/calendar-selection";

interface DateRangeFieldProps {
  className?: string;
  triggerClassName?: string;
  error?: boolean;
  minNights?: number;
  minNightsSchedule?: { nearUntil: string; nearMinNights: number; farMinNights: number } | null;
  onValidationChange?: (isValid: boolean) => void;
  /** Open on mount for any incomplete selection, including restored check-in. */
  autoOpen?: boolean;
  propertySlug?: string;
  /** Increment to open this particular widget's calendar without clearing dates. */
  openRequest?: number;
}

export function DateRangeField({ 
  className,
  triggerClassName,
  error, 
  minNights = 1,  // Callers pass the admin-configured value; 1 matches the server default
  minNightsSchedule,
  onValidationChange,
  autoOpen = false,
  propertySlug: selectedPropertySlug,
  openRequest = 0,
}: DateRangeFieldProps) {
  const minimumForDate = useCallback((ymd: string) =>
    minNightsSchedule
      ? (ymd <= minNightsSchedule.nearUntil ? minNightsSchedule.nearMinNights : minNightsSchedule.farMinNights)
      : minNights, [minNightsSchedule, minNights]);
  const { checkIn, checkOut, updateBookingDates, hasValidDates, propertyCheckoutTime, propertySlug: contextPropertySlug } = useBooking();
  const propertySlug = selectedPropertySlug || contextPropertySlug;
  
  const [isOpen, setIsOpen] = useState(false);
  const [announceText, setAnnounceText] = useState("");
  // Derived, never separately initialized by a particular opening path.
  const activeField = calendarSelectionMode(checkIn, checkOut);
  const [calendarMonth, setCalendarMonth] = useState<Date>(() => parseDateOnly(checkIn) ?? new Date());
  // The day under the pointer while a checkout is being chosen: the range is
  // painted up to it, the way Airbnb previews a stay before the second click.
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  // A short note in the header when a click could not be a checkout.
  const [hint, setHint] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(() => 
    typeof window !== 'undefined' && window.innerWidth < 768
  );

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Ref for focus management
  const triggerRef = useRef<HTMLButtonElement>(null);
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close calendar on Escape key (backdrop click is handled by the overlay div)
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  // The in-calendar Clear control keeps the picker open for a new selection.
  // Clear all (and other external resets) should close it so the homepage form
  // does not keep an empty calendar overlay after dates disappear.
  const keepCalendarOpenOnClearRef = useRef(false);
  useEffect(() => {
    if (checkIn || checkOut) return;
    if (keepCalendarOpenOnClearRef.current) {
      keepCalendarOpenOnClearRef.current = false;
      return;
    }
    setIsOpen(false);
  }, [checkIn, checkOut]);

  // Fetch unavailable dates for calendar with improved error handling
  const { 
    unavailableDatesSet, 
    isLoading: availabilityLoading, 
    isError: availabilityCriticalError,
    hasMinorError: availabilityMinorError,
    hasData: availabilityHasData,
    isDateUnavailable,
    isDateCovered,
    refetch: refetchAvailability
  } = useUnavailableDatesForCalendar(new Date(), 12, {
    enabled: true, // Always fetch for better UX - data is cached
    // Follow the month the guest is viewing so navigation past the base
    // window still gets real availability data instead of all-open dates.
    viewedMonth: calendarMonth,
    propertySlug,
  });

  // Compute blocked date ranges to identify gaps that are too short for minimum nights
  const blockedDateRanges = useMemo(() => {
    if (!availabilityHasData || !unavailableDatesSet) return [];
    
    const sortedDates = Array.from(unavailableDatesSet).sort();
    const ranges: { start: Date; end: Date }[] = [];
    
    let rangeStart: Date | null = null;
    let rangeEnd: Date | null = null;
    
    for (const dateStr of sortedDates) {
      const date = new Date(dateStr + 'T12:00:00');
      
      if (!rangeStart) {
        rangeStart = date;
        rangeEnd = date;
      } else if (rangeEnd) {
        const dayDiff = differenceInDays(date, rangeEnd);
        if (dayDiff <= 1) {
          // Continue the range
          rangeEnd = date;
        } else {
          // End current range and start new one
          ranges.push({ start: rangeStart, end: rangeEnd });
          rangeStart = date;
          rangeEnd = date;
        }
      }
    }
    
    // Don't forget the last range
    if (rangeStart && rangeEnd) {
      ranges.push({ start: rangeStart, end: rangeEnd });
    }
    
    return ranges;
  }, [unavailableDatesSet, availabilityHasData]);

  // Convert string dates to Date objects for the calendar with safe parsing
  const dateRange: DateRange | undefined = useMemo(() => {
    if (checkIn && checkOut) {
      const fromDate = parseDateOnly(checkIn);
      const toDate = parseDateOnly(checkOut);
      
      // Validate dates before using them
      if (fromDate && toDate) {
        return { from: fromDate, to: toDate };
      }
    }
    
    if (checkIn) {
      const fromDate = parseDateOnly(checkIn);
      if (fromDate) {
        return { from: fromDate, to: undefined };
      }
    }
    
    return undefined;
  }, [checkIn, checkOut]);
  const retainedRangeError = checkIn && checkOut && availabilityHasData
    ? validateCalendarStay(checkIn, checkOut, minimumForDate(checkIn), unavailableDatesSet, isDateCovered)
    : null;

  // Earliest checkout for the current check-in, for the header note.
  const earliestCheckoutStr = useMemo(() => {
    if (!checkIn) return "";
    const parsed = parseDateOnly(checkIn);
    return parsed ? format(addDays(startOfDay(parsed), minimumForDate(checkIn)), "yyyy-MM-dd") : "";
  }, [checkIn, minimumForDate]);

  // While a checkout is being chosen, paint the range up to the hovered day.
  const previewRange: DateRange | undefined = useMemo(() => {
    if (checkIn && !checkOut && hoverDate) {
      const parsed = parseDateOnly(checkIn);
      if (parsed) {
        const from = startOfDay(parsed);
        const to = startOfDay(hoverDate);
        if (isAfter(to, from)) return { from, to };
      }
    }
    return dateRange;
  }, [checkIn, checkOut, hoverDate, dateRange]);

  // Validation effect
  useEffect(() => {
    const isValid = hasValidDates() && availabilityHasData && !retainedRangeError;
    onValidationChange?.(isValid);
  }, [checkIn, checkOut, hasValidDates, availabilityHasData, retainedRangeError, onValidationChange]);

  // Format date for display with enhanced safety
  const formatDisplayDate = (date: string) => {
    if (!date || date.trim() === '') return "";
    try {
      const dateObj = parseDateOnly(date);
      if (!dateObj) return "";
      return format(dateObj, "MMM dd");
    } catch (error) {
      console.warn('Date display formatting error:', error);
      return "";
    }
  };

  // Format unified date range display with year derived from booking context
  const getDateRangeDisplay = () => {
    if (checkIn && checkOut) {
      const checkInFormatted = formatDisplayDate(checkIn);
      const checkOutFormatted = formatDisplayDate(checkOut);
      
      if (checkInFormatted && checkOutFormatted) {
        // Derive year from checkout date in booking context to avoid drift
        const displayYear = getYearFromDate(checkOut);
        return `${checkInFormatted} - ${checkOutFormatted}, ${displayYear}`;
      }
    }
    
    if (checkIn) {
      const checkInFormatted = formatDisplayDate(checkIn);
      return checkInFormatted ? `${checkInFormatted} - Add checkout` : "Add dates";
    }
    
    return "Add dates";
  };

  // Explicit Airbnb-style click flow: first click = check-in, second click = check-out.
  // We intentionally ignore react-day-picker's computed range (which can auto-extend
  // selections) and only ever act on the exact day the user clicked.
  const handleDayClick = (_range: DateRange | undefined, clickedDay: Date) => {
    if (!clickedDay || isNaN(clickedDay.getTime())) return;

    try {
      const clicked = startOfDay(clickedDay);
      // Build the date-only "YYYY-MM-DD" string from the exact clicked calendar cell.
      // No Date/UTC/ISO conversion — this string flows unchanged through the entire pipeline.
      const clickedStr = format(clicked, "yyyy-MM-dd");

      const setAsCheckIn = () => {
        if (!availabilityHasData || !isDateCovered(clickedStr) || isDateUnavailable(clickedStr) || isInShortGap(clicked) || isDateInPast(clickedStr)) {
          const message = "This check-in is unavailable for this home. Please select another date.";
          setHint(message);
          setAnnounceText(message);
          return;
        }
        setHint(null);
        updateBookingDates(clickedStr, undefined, true);
        setAnnounceText(`Check-in on ${formatDate(clickedStr)}. Now select your check-out date (${minimumForDate(clickedStr)} night minimum).`);
      };

      // No check-in yet, or restarting after a complete range: this click is the new check-in
      if (!checkIn || (checkIn && checkOut)) {
        setAsCheckIn();
        return;
      }

      // We have a check-in but no check-out yet.
      // Anchor to midnight (parseDateOnly returns local NOON) so the night count
      // matches the calendar's startOfDay candidates — otherwise an exactly
      // minNights checkout counts as one night short and gets wrongly rejected.
      const parsedCheckIn = parseDateOnly(checkIn);
      if (!parsedCheckIn) {
        setAsCheckIn();
        return;
      }
      const checkInDate = startOfDay(parsedCheckIn);

      // Clicking on or before the current check-in restarts selection from that day
      if (!isAfter(clicked, checkInDate)) {
        setAsCheckIn();
        return;
      }

      // Candidate check-out: enforce minimum nights (defensive; such dates are also disabled)
      const nightsSelected = differenceInDays(clicked, checkInDate);
      const rangeError = validateCalendarStay(checkIn, clickedStr, minimumForDate(checkIn), unavailableDatesSet, isDateCovered);
      if (rangeError) {
        const message = rangeError;
        setHint(message);
        setAnnounceText(message);
        return;
      }

      // Both dates are plain date-only strings — check-in stays as stored, checkout is the clicked day.
      setHint(null);
      setHoverDate(null);
      updateBookingDates(checkIn, clickedStr, true);
      setAnnounceText(`Your stay is set: ${formatDate(checkIn)} to ${formatDate(clickedStr)}, ${nightsSelected} nights.`);
      // Both dates chosen: close, the way a listing's date picker does once
      // the stay is set. The trigger shows the dates and reopens on tap.
      window.setTimeout(() => setIsOpen(false), 320);
    } catch (error) {
      console.warn('Date selection error:', error);
      setAnnounceText('Error selecting date. Please try again.');
    }
  };

  // Handle clearing dates with enhanced feedback
  const handleClear = () => {
    keepCalendarOpenOnClearRef.current = true;
    updateBookingDates("", "", false);
    setAnnounceText("Dates cleared. Ready to select new dates.");
    setHint(null);
    setHoverDate(null);
    // Keep calendar open for immediate new selection
  };

  // Handle unified trigger click with enhanced UX
  const handleTriggerClick = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    // Set active field and provide clear user guidance
    if (!checkIn) {
      setAnnounceText('Calendar opened. Please select your check-in date.');
    } else if (!checkOut) {
      setAnnounceText('Please select your check-out date.');
    } else {
      // Both dates exist, provide option to modify with clear guidance
      setAnnounceText('Modify your dates by selecting a new check-in date.');
    }
    setCalendarMonth(parseDateOnly(checkIn) ?? new Date());
    setHint(null);
    setHoverDate(null);
    setIsOpen(true);
  };

  // Find the next blocked date after a given date
  const getNextBlockedDate = useCallback((fromDate: Date): Date | null => {
    if (!availabilityHasData || !unavailableDatesSet) return null;
    
    const today = startOfDay(new Date());
    let checkDate = startOfDay(fromDate);
    
    // Look ahead up to 365 days
    for (let i = 0; i < 365; i++) {
      checkDate = addDays(checkDate, 1);
      const dateStr = format(checkDate, "yyyy-MM-dd");
      if (unavailableDatesSet.has(dateStr)) {
        return checkDate;
      }
    }
    return null;
  }, [unavailableDatesSet, availabilityHasData]);

  // Check if a date is in a gap that's too short for minimum nights.
  // Works independently of backend short-gap computation:
  //   1. Forward check: next blocked date is < minNights away → cannot start a stay here
  //   2. Backward check: previous blocked date is within (minNights-1) days AND the
  //      total enclosed gap length < minNights → all dates in the gap are un-bookable
  const isInShortGap = useCallback((date: Date): boolean => {
    if (!availabilityHasData || !unavailableDatesSet) return false;
    
    const dateOnly = startOfDay(date);
    const dateStr = format(dateOnly, "yyyy-MM-dd");
    
    // If the date itself is blocked, it's not in a "gap"
    if (unavailableDatesSet.has(dateStr)) return false;
    
    // Find the next blocked date after this one
    const nextBlocked = getNextBlockedDate(dateOnly);
    if (!nextBlocked) return false; // No upcoming blocked date, no short gap
    
    // Forward check: fewer available days than minimum stay
    const daysUntilBlocked = differenceInDays(nextBlocked, dateOnly);
    const dateMinimum = minimumForDate(dateStr);
    if (daysUntilBlocked < dateMinimum) return true;
    
    // Backward check: find if there is an enclosed gap shorter than minNights.
    // Scan back up to (minNights - 1) days to detect an adjacent left boundary.
    for (let i = 1; i < dateMinimum; i++) {
      const prevDate = addDays(dateOnly, -i);
      const prevDateStr = format(prevDate, "yyyy-MM-dd");
      if (unavailableDatesSet.has(prevDateStr)) {
        // Enclosed gap: prevDate is blocked, nextBlocked is the right boundary.
        // Total gap = dates strictly between prevDate and nextBlocked.
        const gapLength = differenceInDays(nextBlocked, prevDate) - 1;
        return gapLength < dateMinimum;
      }
    }
    
    return false;
  }, [availabilityHasData, unavailableDatesSet, getNextBlockedDate, minimumForDate]);

  // Find the first available date for booking (not past, not unavailable, not in short gap)
  // Starts from tomorrow — today is never a valid "next available" check-in
  const getFirstAvailableDate = useCallback((): Date | null => {
    if (!availabilityHasData) return null;
    const today = startOfDay(new Date());
    let checkDate = addDays(today, 1); // start from tomorrow
    
    // Look ahead up to 365 days
    for (let i = 0; i < 365; i++) {
      const dateStr = format(checkDate, "yyyy-MM-dd");
      
      const isUnavailable = availabilityHasData && unavailableDatesSet?.has(dateStr);
      const inShortGap = isInShortGap(checkDate);
      
      if (isDateCovered(dateStr) && !isUnavailable && !inShortGap) {
        return checkDate;
      }
      
      checkDate = addDays(checkDate, 1);
    }
    
    return null;
  }, [availabilityHasData, unavailableDatesSet, isInShortGap, isDateCovered]);

  // Handler to jump to the first available date
  const handleJumpToAvailable = useCallback(() => {
    const firstAvailable = getFirstAvailableDate();
    if (firstAvailable) {
      setCalendarMonth(firstAvailable);
      setAnnounceText(`Jumped to ${format(firstAvailable, 'MMMM yyyy')}. First available date is ${format(firstAvailable, 'MMMM d, yyyy')}.`);
    } else {
      setAnnounceText('No available dates found in the next 12 months.');
    }
  }, [getFirstAvailableDate]);

  // Memoized date disabling function for performance
  const isDateDisabled = useCallback((date: Date) => {
    if (!date || isNaN(date.getTime())) return true;
    
    const dateOnly = startOfDay(date);
    let dateStr: string;
    try {
      dateStr = format(dateOnly, "yyyy-MM-dd");
    } catch (error) {
      return true;
    }
    
    // PRIORITY 1: ALWAYS block past dates (cannot book yesterday)
    if (isBefore(dateOnly, startOfDay(new Date()))) {
      return true;
    }

    if (!isDateCovered(dateStr)) return true;
    if (activeField === "checkout" && checkIn && dateStr > checkIn) {
      return validateCalendarStay(checkIn, dateStr, minimumForDate(checkIn), unavailableDatesSet, isDateCovered) !== null;
    }
    
    // PRIORITY 2: Check API availability data for blocked dates (Airbnb sync, existing bookings)
    if (availabilityHasData) {
      if (isDateUnavailable(dateStr)) {
        return true;
      }
    } else {
      // No availability data yet (still loading, or the fetch failed):
      // never present dates as bookable — a wrongly-open calendar lets
      // guests select already-booked nights. The footer shows a loading
      // spinner or an error + Retry for these states.
      return true;
    }
    
    // PRIORITY 3: For CHECK-IN selection - block dates that are in gaps too short for minimum nights
    // This prevents users from selecting a check-in date when there aren't enough 
    // consecutive available nights before the next booking
    if (!checkIn || activeField === 'checkin') {
      if (isInShortGap(dateOnly)) {
        return true;
      }
    }
    
    return false;
  }, [checkIn, activeField, availabilityHasData, isDateUnavailable, isInShortGap, unavailableDatesSet, isDateCovered, minimumForDate]);

  // Minimum-stay checkouts are disabled and dimmed, not styled as booked.
  const isMinStayCheckoutCandidate = useCallback((date: Date): boolean => {
    if (!checkIn || checkOut || activeField !== 'checkout') return false;
    const parsedCheckIn = parseDateOnly(checkIn);
    if (!parsedCheckIn) return false;
    // Anchor to midnight to match the calendar's startOfDay candidates (see
    // isDateDisabled) — parseDateOnly returns local noon and would otherwise
    // dim the exactly-minNights checkout date by one day.
    const checkInDate = startOfDay(parsedCheckIn);
    const dateOnly = startOfDay(date);
    if (!isAfter(dateOnly, checkInDate)) return false;
    // Booked dates keep their own styling
    try {
      const dateStr = format(dateOnly, "yyyy-MM-dd");
      if (unavailableDatesSet?.has(dateStr)) return false;
    } catch {
      return false;
    }
    return isBefore(dateOnly, addDays(checkInDate, minimumForDate(checkIn)));
  }, [checkIn, checkOut, activeField, unavailableDatesSet, minimumForDate]);

  // Open nights that cannot START a stay: the next reservation (or its turnover)
  // is fewer than minNights away. They are disabled while a check-in is being
  // chosen, but they are not booked, and most of them work as a checkout day, so
  // they get the dimmed style rather than the struck-through "booked" one.
  const isShortGapCheckinCandidate = useCallback((date: Date): boolean => {
    if (checkIn && activeField !== 'checkin') return false;
    if (!availabilityHasData) return false;
    const dateOnly = startOfDay(date);
    if (isBefore(dateOnly, startOfDay(new Date()))) return false;
    try {
      if (unavailableDatesSet?.has(format(dateOnly, "yyyy-MM-dd"))) return false;
    } catch {
      return false;
    }
    return isInShortGap(dateOnly);
  }, [checkIn, activeField, availabilityHasData, unavailableDatesSet, isInShortGap]);

  // Whether the months on screen contain any such nights, so the hint only
  // appears when there is something to explain.
  const visibleShortGap = useMemo(() => {
    if (!availabilityHasData) return false;
    const months = isMobile ? 1 : 2;
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(addMonths(start, months - 1));
    for (let d = start; !isAfter(d, end); d = addDays(d, 1)) {
      if (isShortGapCheckinCandidate(d)) return true;
    }
    return false;
  }, [availabilityHasData, isMobile, calendarMonth, isShortGapCheckinCandidate]);

  // Check if we have any dates selected
  const hasDatesSelected = Boolean(checkIn || checkOut);
  const hasCompleteDates = Boolean(checkIn && checkOut);

  // Style classes for Airbnb-like appearance
  const triggerBaseClasses = cn(
    "h-14 px-4 justify-start text-left font-normal border rounded-lg",
    "hover:bg-muted/50 focus:ring-2 focus:ring-offset-2 focus:ring-ring",
    "transition-all duration-200 relative overflow-hidden",
    "bg-background border-border",
    error && "border-destructive focus:ring-destructive"
  );

  const labelClasses = "text-luxury-xs font-serif font-normal text-muted-foreground uppercase tracking-luxury-tight";

  // Shared trigger button content
  const triggerButtonContent = (
    <div className="flex items-center justify-between w-full">
      <div className="flex flex-col items-start">
        <span className={cn(labelClasses, "mb-1")}>Dates</span>
        <span className={cn(
          "text-luxury-base font-serif font-normal group-hover:text-foreground transition-colors",
          hasCompleteDates ? "text-foreground" : "text-muted-foreground"
        )}>
          {getDateRangeDisplay()}
        </span>
      </div>
      
      <Calendar className={cn(
        "h-5 w-5 text-muted-foreground group-hover:text-foreground",
        "transition-colors duration-200",
        isOpen && "text-foreground"
      )} />
    </div>
  );

  const triggerButtonProps = {
    type: "button" as const,
    variant: "ghost" as const,
    className: cn(
      "h-16 px-6 justify-between text-left font-normal border rounded-xl",
      "hover:bg-muted/50 hover:shadow-lg focus:ring-2 focus:ring-offset-2 focus:ring-ring",
      "transition-all duration-200 relative overflow-hidden group",
      "bg-background border-border w-full",
      "hover:border-foreground/20",
      error && "border-destructive focus:ring-destructive",
      triggerClassName,
    ),
    onClick: handleTriggerClick,
    "data-testid": "date-range-trigger",
    // Accessible name is derived from the visible "Dates" caption + selected range
    // so the visible label matches the accessible name (WCAG 2.5.3). Extra context
    // is provided via aria-describedby below.
    "aria-describedby": "date-range-help",
  };

  // A link can land with the calendar already open ("Check dates" on a home
  // card): /properties/<slug>/book?open=dates. Read once on mount.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (new URLSearchParams(window.location.search).get("open") === "dates") setIsOpen(true);
    else if (autoOpen && !(checkIn && checkOut)) setIsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!openRequest) return;
    setCalendarMonth(parseDateOnly(checkIn) ?? new Date());
    setHint(null);
    setHoverDate(null);
    setIsOpen(true);
  }, [openRequest]);

  // URL hydration can occur after mount. Follow the actual restored check-in.
  useEffect(() => {
    if (!isOpen) return;
    if (checkIn) setCalendarMonth(parseDateOnly(checkIn) ?? new Date());
  }, [checkIn, propertySlug]);

  // Keep the page from scrolling behind the open calendar.
  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [isOpen]);

  // Shared calendar panel (used in both desktop dropdown and mobile Sheet)
  const calendarPanel = (
    <div className="bg-white overflow-hidden" ref={calendarContainerRef}>
      {/* Header with selection state */}
      <div className="px-5 py-4 border-b border-border bg-gradient-to-b from-muted/20 to-transparent">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-luxury-base font-serif font-normal text-foreground mb-1">
              {hasCompleteDates ? (
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-sand/60 rounded-full flex-shrink-0"></span>
                  {checkIn && checkOut ? `${formatDate(checkIn)} → ${formatDate(checkOut)}` : 'Select dates'}
                </span>
              ) : checkIn ? (
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-ink rounded-full animate-pulse flex-shrink-0"></span>
                  Check-in: {formatDate(checkIn)}
                </span>
              ) : (
                <span className="text-muted-foreground">Select your dates</span>
              )}
            </div>
            <div className={`text-luxury-sm ${hint ? "text-amber-800" : "text-muted-foreground"}`} aria-live="polite">
              {hint || (!checkIn
                ? 'Choose your check-in date'
                : !checkOut
                  ? (availabilityHasData && isDateUnavailable(checkIn)
                    ? "This check-in is unavailable for this home. Clear or choose another check-in."
                    : `Now choose your check-out date (${minimumForDate(checkIn)}-night minimum, ${formatDate(earliestCheckoutStr)} or later)`)
                  : (retainedRangeError ?? 'Tap any date to change your stay'))}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {hasCompleteDates && (
              <span className="inline-flex items-center gap-1 bg-sand/50 text-ink px-3 py-1.5 rounded-full text-luxury-sm font-normal">
                {calculateNights(checkIn, checkOut)} {calculateNights(checkIn, checkOut) === 1 ? 'night' : 'nights'}
              </span>
            )}
            {hasDatesSelected && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="text-luxury-sm text-muted-foreground hover:text-foreground px-3 py-1.5 h-auto rounded-full"
                data-testid="button-clear"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Minimum nights indicator and jump to available button */}
      <div className="px-5 py-2.5 bg-sand/50 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-ink" />
          <span className="text-luxury-sm font-serif text-ink">
            {minNights} night minimum stay
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleJumpToAvailable}
          className="text-luxury-xs font-serif text-ink hover:text-ink/70 hover:bg-sand/50 px-2 py-1 h-auto rounded-full flex items-center gap-1"
          data-testid="jump-to-available"
        >
          <span>Next available</span>
          <ArrowRight className="w-3 h-3" />
        </Button>
      </div>

      {/* Calendar */}
      <div className="px-5 py-4 select-none overflow-hidden">
        <CalendarComponent
          mode="range"
          selected={previewRange}
          onSelect={handleDayClick}
          onDayMouseEnter={(day) => setHoverDate(day)}
          onDayMouseLeave={() => setHoverDate(null)}
          numberOfMonths={isMobile ? 1 : 2}
          disabled={isDateDisabled}
          modifiers={{ minStay: isMinStayCheckoutCandidate, shortGap: isShortGapCheckinCandidate }}
          modifiersClassNames={{ minStay: "rdp-day_minStay", shortGap: "rdp-day_shortGap" }}
          showOutsideDays={false}
          className="border-0 w-full"
          month={calendarMonth}
          onMonthChange={setCalendarMonth}
          fromMonth={new Date()}
          classNames={{
            months: "flex flex-col sm:flex-row gap-4 sm:gap-8 justify-center",
            month: "space-y-3",
            caption: "flex justify-center pt-1 relative items-center h-10 mb-2",
            caption_label: "text-luxury-base font-serif font-normal text-foreground",
            nav: "flex items-center",
            nav_button: cn(
              "inline-flex items-center justify-center h-8 w-8 bg-transparent p-0",
              "border border-border rounded-full opacity-70 hover:opacity-100",
              "hover:bg-muted/80 transition-all duration-150 ease-out cursor-pointer",
              "active:scale-95"
            ),
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            table: "w-full border-collapse",
            head_row: "flex justify-between",
            head_cell: cn(
              "text-muted-foreground w-9 font-serif font-normal text-luxury-xs py-2 text-center"
            ),
            row: "flex w-full justify-between mt-0.5",
            cell: cn(
              "h-9 w-9 text-center text-luxury-sm p-0 relative",
              "transition-colors duration-75 ease-out",
              "[&:has([aria-selected].day-range-end)]:rounded-r-full",
              "[&:has([aria-selected].day-range-start)]:rounded-l-full",
              "[&:has([aria-selected])]:bg-sand/50",
              "focus-within:relative focus-within:z-20"
            ),
            day: cn(
              "h-9 w-9 p-0 font-serif font-normal text-luxury-sm rounded-full",
              "hover:bg-muted/80 focus:outline-none",
              "aria-selected:opacity-100",
              "transition-all duration-100 ease-out",
              "active:scale-95"
            ),
            day_range_start: cn(
              "day-range-start bg-ink text-white rounded-full",
              "hover:bg-ink/90 hover:text-white"
            ),
            day_range_end: cn(
              "day-range-end bg-ink text-white rounded-full",
              "hover:bg-ink/90 hover:text-white"
            ),
            day_selected: cn(
              "bg-ink text-white rounded-full",
              "hover:bg-ink/90 hover:text-white"
            ),
            // Today is not marked: a ring on today read as a pre-selected date.
            day_today: "text-ink font-medium",
            day_outside: "text-muted-foreground/20 aria-selected:bg-transparent",
            day_disabled: "rdp-day_disabled",
            day_range_middle: cn(
              "aria-selected:bg-sand/50 aria-selected:text-foreground rounded-none"
            ),
            day_hidden: "invisible",
          }}
          data-testid="calendar-range"
          aria-label="Select date range for your stay"
        />
      </div>

      {/* Footer with status */}
      <div className="px-5 py-3 border-t border-border bg-muted/10">
        {availabilityLoading && (
          <div className="flex items-center justify-center gap-2 text-luxury-sm text-muted-foreground">
            <div className="w-4 h-4 border-2 border-sand-deep border-t-transparent rounded-full animate-spin" />
            Loading availability...
          </div>
        )}
        
        {availabilityCriticalError && !availabilityLoading && (
          <div className="flex items-center justify-between text-luxury-sm">
            <span className="text-destructive">Unable to load availability</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => refetchAvailability()}
              className="text-luxury-sm h-auto px-3 py-1"
            >
              Retry
            </Button>
          </div>
        )}
        
        {!availabilityLoading && !availabilityCriticalError && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-luxury-sm text-muted-foreground min-w-0">
              {!checkIn ? (
                visibleShortGap ? (
                  <span>
                    Tap an available date to start.{' '}
                    <span className="text-ink/70">Dimmed dates are open but sit too close to the next reservation to begin a {minNights}-night stay; they can still be your checkout day.</span>
                  </span>
                ) : (
                  'Tap an available date to start'
                )
              ) : !checkOut ? (
                <span className="text-ink">Select checkout (min. {minNights} nights from check-in)</span>
              ) : (
                <span className="text-palm flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-sand/60 rounded-full"></span>
                  <span>
                    {retainedRangeError ? "Dates unavailable — change your stay" : "Dates selected"}
                    {!retainedRangeError && propertyCheckoutTime && (
                      <span className="text-muted-foreground"> · Checkout by {propertyCheckoutTime}</span>
                    )}
                  </span>
                </span>
              )}
            </span>

            <Button
              type="button"
              variant={hasCompleteDates ? "bronze" : "outline"}
              size="sm"
              onClick={() => setIsOpen(false)}
              className="font-serif font-normal rounded-full px-5 shrink-0"
              data-testid="button-done"
              aria-label={hasCompleteDates ? "Done, close calendar" : "Close calendar"}
            >
              {hasCompleteDates ? 'Done' : 'Close'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={cn("relative", className)} ref={wrapperRef}>
      {/* Screen reader announcements */}
      <div 
        aria-live="polite" 
        aria-atomic="true"
        className="sr-only"
        role="status"
      >
        {announceText}
      </div>

      <div id="date-range-help" className="sr-only">
        Select your check-in and check-out dates. Choose your arrival date first, then your departure date.
      </div>

      {/* Trigger button — same on all devices */}
      <Button {...triggerButtonProps}>
        {triggerButtonContent}
      </Button>

      {/* Centered modal, the same on every device. Rendered into <body> through a
          portal: inside the page it sat under the sticky booking widget, whose
          reveal animation leaves a transform on an ancestor, and a fixed element
          inside a transformed ancestor is positioned against that ancestor, not
          the viewport. That put the calendar below the fold. */}
      {isOpen && typeof document !== "undefined" && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[200] bg-black/50"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          {/* Calendar panel */}
          <div
            data-testid="calendar-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Select dates"
            className="fixed z-[201] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{
              width: 'min(95vw, 680px)',
              maxHeight: 'min(90vh, 90dvh)',
              overflowY: 'auto',
            }}
          >
            <div className="rounded-2xl border border-border overflow-hidden shadow-2xl bg-background">
              {calendarPanel}
            </div>
          </div>
        </>,
        document.body,
      )}

      {/* Error message */}
      {error && (
        <div 
          className="mt-1 text-luxury-sm font-serif font-normal text-destructive"
          role="alert"
          data-testid="error-message"
        >
          Please select valid check-in and check-out dates
        </div>
      )}
    </div>
  );
}